import { useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import "react-loading-skeleton/dist/skeleton.css";
import toast from "react-hot-toast";

import {
  getAddress,
  editAddress,
} from "../../../Account/address/redux/addressSlice";

import { checkPincode } from "../../../Products/pdp/redux/pincodeSlice";

const EditAddressModel = ({
  editAddressData,
  setEditAddressData,
  setShowEditAddressModal,
}) => {
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});

  const { pinset } = useSelector((state) => state.pincode);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (
      pinset?.city &&
      pinset?.state &&
      editAddressData?.pincode?.length === 6
    ) {
      setEditAddressData((prev) => ({
        ...prev,
        city: pinset.city,
        state: pinset.state,
      }));
    }
  }, [pinset]);

  const validateAddressForm = (form) => {
    let formErrors = {};

    if (!form?.first_name?.trim()) {
      formErrors.first_name = "First name is required";
    } else if (!/^[A-Za-z\s]+$/.test(form.first_name)) {
      formErrors.first_name = "Only alphabets are allowed";
    } else if (form.first_name.trim().length < 3) {
      formErrors.first_name =
        "First name must be at least 3 characters long";
    }

    if (!form?.last_name?.trim()) {
      formErrors.last_name = "Last name is required";
    } else if (!/^[A-Za-z\s]+$/.test(form.last_name)) {
      formErrors.last_name = "Only alphabets are allowed";
    } else if (form.last_name.trim().length < 3) {
      formErrors.last_name =
        "Last name must be at least 3 characters long";
    }

    if (!form?.mobile?.trim()) {
      formErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      formErrors.mobile = "Enter valid mobile number";
    }

    if (!form?.address?.trim()) {
      formErrors.address = "Address is required";
    }

    if (!form?.address2?.trim()) {
      formErrors.address2 = "Address 2 is required";
    }

    if (!form?.landmark?.trim()) {
      formErrors.landmark = "Landmark is required";
    }

    if (!form?.city?.trim()) {
      formErrors.city = "City is required";
    }

    if (!form?.state?.trim()) {
      formErrors.state = "State is required";
    }

    if (!form?.pincode?.trim()) {
      formErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(form.pincode)) {
      formErrors.pincode = "Enter valid 6 digit pincode";
    }

    setErrors(formErrors);

    return Object.keys(formErrors).length === 0;
  };

  const handleEditAddressChange = (e) => {
    const { name, value } = e.target;

    setEditAddressData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "pincode") {
      if (value.length === 6) {
        dispatch(checkPincode(value));
      }

      if (value.length < 6) {
        setEditAddressData((prev) => ({
          ...prev,
          pincode: value,
          city: "",
          state: "",
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAddressForm(editAddressData)) return;

    const result = await dispatch(
      editAddress({
        id: editAddressData.id,
        data: editAddressData,
      })
    );

    if (result.meta.requestStatus === "fulfilled") {
      dispatch(getAddress());

      toast.success("Address Updated Successfully");

      setShowEditAddressModal(false);
      setEditAddressData(null);
      setErrors({});
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => setShowEditAddressModal(false)}
    >
      <div
        className="side-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="close-btn"
          onClick={() => {
            setShowEditAddressModal(false);
            setEditAddressData(null);
            setErrors({});
          }}
        >
          <IoMdClose size={25} />
        </button>

        <h3>Edit Address</h3>

        <form onSubmit={handleSubmit}>
          <label>
            <span>First Name <span className="required">*</span> </span>
            <input
              type="text"
              name="first_name"
              value={editAddressData?.first_name || ""}
              onChange={handleEditAddressChange}
            />
            {errors.first_name && (
              <p className="error">{errors.first_name}</p>
            )}
          </label>

          <label>
            <span>Last Name <span className="required">*</span> </span>
            <input
              type="text"
              name="last_name"
              value={editAddressData?.last_name || ""}
              onChange={handleEditAddressChange}
            />
            {errors.last_name && (
              <p className="error">{errors.last_name}</p>
            )}
          </label>

          <label>
            <span>Mobile <span className="required">*</span> </span>
            <input
              type="text"
              maxLength={10}
              name="mobile"
              value={editAddressData?.mobile || ""}
              onChange={handleEditAddressChange}
            />
            {errors.mobile && (
              <p className="error">{errors.mobile}</p>
            )}
          </label>

          <label>
            <span>Pincode <span className="required">*</span> </span>
            <input
              type="text"
              maxLength={6}
              name="pincode"
              value={editAddressData?.pincode || ""}
              onChange={handleEditAddressChange}
            />
            {errors.pincode && (
              <p className="error">{errors.pincode}</p>
            )}
          </label>

          <label>
            <span>State <span className="required">*</span> </span>
            <input
              type="text"
              name="state"
              value={editAddressData?.state || ""}
              onChange={handleEditAddressChange}
            />
            {errors.state && (
              <p className="error">{errors.state}</p>
            )}
          </label>

          <label>
            <span>City <span className="required">*</span></span>
            <input
              type="text"
              name="city"
              value={editAddressData?.city || ""}
              onChange={handleEditAddressChange}
            />
            {errors.city && (
              <p className="error">{errors.city}</p>
            )}
          </label>

          <label>
            <span>Street Address 1 <span className="required">*</span> </span>
            <input
              type="text"
              name="address"
              value={editAddressData?.address || ""}
              onChange={handleEditAddressChange}
            />
            {errors.address && (
              <p className="error">{errors.address}</p>
            )}
          </label>

          <label>
            <span>Street Address 2 <span className="required">*</span></span>
            <input
              type="text"
              name="address2"
              value={editAddressData?.address2 || ""}
              onChange={handleEditAddressChange}
            />
            {errors.address2 && (
              <p className="error">{errors.address2}</p>
            )}
          </label>

          <label>
            <span>Landmark <span className="required">*</span> </span>
            <input
              type="text"
              name="landmark"
              value={editAddressData?.landmark || ""}
              onChange={handleEditAddressChange}
            />
            {errors.landmark && (
              <p className="error">{errors.landmark}</p>
            )}
          </label>

          <button type="submit">
            Update Address
          </button>
        </form>
      </div>
    </div>
  );
};
export default EditAddressModel;